import { useEffect, useId, useMemo, useRef, type CSSProperties } from 'react';
import { Wrench, type LucideIcon } from 'lucide-react';
import {
  AGENT_AVATAR_ENERGY_SPEED,
  agentAvatarRhythm,
  renderAgentAvatarSvg,
  resolveAgentAvatar,
} from '@fyso/shared/agent-avatar';
import './agent-orb.css';

/** What the agent is doing; each maps to a run/status event. */
export type AgentOrbState =
  | 'idle' | 'listening' | 'thinking' | 'working' | 'speaking' | 'done' | 'error' | 'sleeping';

export const AGENT_ORB_STATES: Record<AgentOrbState, { label: string; when: string; event: string }> = {
  idle: { label: 'En reposo', when: 'Habilitado, esperando.', event: 'status: active' },
  listening: { label: 'Escuchando', when: 'Le llegó un mensaje.', event: 'message.received' },
  thinking: { label: 'Pensando', when: 'Decide qué hacer.', event: 'run.step' },
  working: { label: 'Usando herramienta', when: 'Consulta o escribe datos.', event: 'tool.call' },
  speaking: { label: 'Respondiendo', when: 'Escribe o habla.', event: 'run.output' },
  done: { label: 'Listo', when: 'Terminó la tarea.', event: 'run.completed' },
  error: { label: 'Con problema', when: 'Algo falló; revisá la actividad.', event: 'run.failed' },
  sleeping: { label: 'En pausa', when: 'Pausado o sin señal.', event: 'status: paused' },
};

const FACE: Record<AgentOrbState, { expr: string; mouth: string }> = {
  idle: { expr: '', mouth: 'smile' },
  listening: { expr: 'wide', mouth: 'o' },
  thinking: { expr: '', mouth: 'flat' },
  working: { expr: 'focus', mouth: 'flat' },
  speaking: { expr: '', mouth: 'talk' },
  done: { expr: 'happy', mouth: 'smile' },
  error: { expr: 'worried', mouth: 'flat' },
  sleeping: { expr: 'sleep', mouth: 'flat' },
};

// One pointer listener for every orb on the page.
const pointer = { x: -1e4, y: -1e4 };
let pointerListeners = 0;
const onPointer = (e: PointerEvent) => { pointer.x = e.clientX; pointer.y = e.clientY; };

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export interface AgentOrbProps {
  /** Stored recipe (any shape; invalid or null falls back to the default for `agentId`). */
  avatar?: unknown;
  agentId: string;
  size?: number;
  state?: AgentOrbState;
  /** Icon orbiting the orb while `working`. */
  toolIcon?: LucideIcon;
  /** false = static (no blinking or gaze), for thumbnails. */
  alive?: boolean;
  className?: string;
  /** Accessible name; defaults to the state label. Pass '' when the name is next to it. */
  label?: string;
}

export function AgentOrb({
  avatar, agentId, size = 48, state = 'idle', toolIcon: ToolIcon = Wrench, alive = true, className, label,
}: AgentOrbProps) {
  const recipe = useMemo(() => resolveAgentAvatar(avatar, agentId), [avatar, agentId]);
  const rawId = useId();
  const svg = useMemo(
    () => renderAgentAvatarSvg(recipe, { idPrefix: `orb${rawId}` }),
    [recipe, rawId],
  );
  const speed = AGENT_AVATAR_ENERGY_SPEED[recipe.energy ?? 'normal'];
  const rhythm = agentAvatarRhythm(recipe.seed);
  const root = useRef<HTMLSpanElement>(null);
  const pose = useRef<HTMLSpanElement>(null);
  const stateRef = useRef(state);
  const firstState = useRef(true);

  // Blinks and glances run outside React: they only touch CSS variables.
  useEffect(() => {
    if (!alive || prefersReducedMotion()) return;
    const el = root.current;
    if (!el) return;
    if (pointerListeners++ === 0) window.addEventListener('pointermove', onPointer, { passive: true });
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (fn: () => void, ms: number) => {
      const t = setTimeout(() => { timers.delete(t); fn(); }, ms);
      timers.add(t);
    };
    const gaze = () => el.querySelector<SVGGElement>('.gaze');
    const look = (x: number, y: number) => {
      gaze()?.style.setProperty('--gx', `${x.toFixed(2)}px`);
      gaze()?.style.setProperty('--gy', `${y.toFixed(2)}px`);
      el.style.setProperty('--lr', `${(x * 0.9).toFixed(2)}deg`);
    };
    const glance = () => {
      const s = stateRef.current;
      if (s === 'sleeping') return look(0, 1);
      if (s === 'thinking') return look(2.5 + Math.random() * 1.5, -3.5 - Math.random());
      if (s === 'working') return look((Math.random() - 0.5) * 2, 2.5);
      const r = el.getBoundingClientRect();
      const dx = pointer.x - (r.left + r.width / 2), dy = pointer.y - (r.top + r.height / 2);
      const d = Math.hypot(dx, dy);
      if (d < 340 && d > 0) return look((dx / d) * 4.2, (dy / d) * 3.4);
      if (Math.random() < 0.35) look(0, 0);
      else look((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 4);
    };
    const blink = () => {
      if (stateRef.current === 'sleeping' || stateRef.current === 'done') return;
      const once = () => { el.classList.add('is-blinking'); later(() => el.classList.remove('is-blinking'), 120); };
      once();
      if (Math.random() < 0.2) later(once, 240);
    };
    const loop = (fn: () => void, min: number, max: number) => {
      later(() => { fn(); loop(fn, min, max); }, (min + Math.random() * (max - min)) / speed);
    };
    loop(blink, 2200, 5200);
    loop(glance, 700, 2400);
    const onMove = () => { if (!['sleeping', 'thinking', 'working'].includes(stateRef.current)) glance(); };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('pointermove', onMove);
      if (--pointerListeners === 0) window.removeEventListener('pointermove', onPointer);
    };
  }, [alive, speed]);

  // One-shot reactions when the state changes.
  useEffect(() => {
    stateRef.current = state;
    if (firstState.current) { firstState.current = false; return; }
    const target = pose.current;
    if (!target || prefersReducedMotion() || typeof target.animate !== 'function') return;
    const spring = { easing: 'cubic-bezier(.3,.7,.4,1.3)' };
    if (state === 'done') {
      target.animate([{ transform: 'none' }, { transform: 'translateY(3%) scale(1.08,.9)' },
        { transform: 'translateY(-16%) scale(.95,1.06)' }, { transform: 'none' }], { duration: 620, ...spring });
      sparkle(root.current);
    }
    if (state === 'error') {
      target.animate([0, -6, 6, -4, 4, -2, 0].map((x) => ({ transform: `translateX(${x}%) rotate(${x / 2}deg)` })), { duration: 520 });
    }
    if (state === 'listening') {
      target.animate([{ transform: 'none' }, { transform: 'scale(1.07)' }, { transform: 'none' }], { duration: 380, ...spring });
    }
  }, [state]);

  const face = FACE[state];
  const style = {
    '--size': `${size}px`, '--k': speed, '--float': `${rhythm.float.toFixed(2)}s`, '--phase': `${rhythm.phase.toFixed(2)}s`,
  } as CSSProperties;

  return (
    <span
      ref={root}
      className={`agent-orb${alive ? '' : ' is-still'}${className ? ` ${className}` : ''}`}
      data-state={state}
      data-expr={face.expr}
      data-mouth={face.mouth}
      style={style}
      role={label === '' ? undefined : 'img'}
      aria-hidden={label === '' ? true : undefined}
      aria-label={label === '' ? undefined : (label ?? AGENT_ORB_STATES[state].label)}
    >
      <span className="agent-orb__shadow" />
      <span className="agent-orb__body">
        <span className="agent-orb__lean">
          <span ref={pose} className="agent-orb__pose" dangerouslySetInnerHTML={{ __html: svg }} />
        </span>
      </span>
      <span className="agent-orb__fx">
        <span className="agent-orb__think"><i /><i /><i /></span>
        <span className="agent-orb__orbit"><span className="agent-orb__sat"><ToolIcon /></span></span>
        <span className="agent-orb__waves"><i /><i /></span>
        <span className="agent-orb__ring" />
        <span className="agent-orb__zzz"><i>z</i><i>z</i></span>
      </span>
    </span>
  );
}

function sparkle(el: HTMLElement | null) {
  const fx = el?.querySelector('.agent-orb__fx');
  if (!el || !fx) return;
  const colors = ['#fde68a', '#a7f3d0', '#f0abfc', '#93c5fd'];
  for (let i = 0; i < 7; i++) {
    const s = document.createElement('span');
    s.className = 'agent-orb__spark';
    s.style.setProperty('--c', colors[i % colors.length]);
    fx.append(s);
    const a = (i / 7) * Math.PI * 2 + Math.random() * 0.4;
    const dist = el.offsetWidth * (0.6 + Math.random() * 0.25);
    const anim = s.animate(
      [{ transform: 'translate(0,0) scale(.3)', opacity: 1 },
        { transform: `translate(${Math.cos(a) * dist}px,${Math.sin(a) * dist}px) scale(1) rotate(90deg)`, opacity: 0 }],
      { duration: 760, easing: 'cubic-bezier(.2,.7,.3,1)' },
    );
    anim.onfinish = () => s.remove();
  }
}

export default AgentOrb;
